import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Typography, Box, Paper, Button} from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";
import dayjs from "dayjs";
import {useServices} from "../providers/service-provider";
import Lottie from "react-lottie";
import CustomMenu from "../Menu";
import MealsWithGraph from "../ListView/MealsWithGraph";
import DayDataView from "./DayDataView";
import * as food_carousel from "../images/food_carousel.json";
import Maps3 from "../Maps/Maps3";
import {calculateTimeInRange} from "../utils/timeInRange";

// Define a constant drawerWidth that should match CustomMenu's width.
const drawerWidth = 320;

const DataAnalyizer = (props) => {
    const {user, isAdmin, userRole} = props;
    const [data, setData] = useState([]);
    const [startDate, setStartDate] = useState(dayjs().startOf("month"));
    const [endDate, setEndDate] = useState(dayjs().endOf('day'));
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const {authorizedHttpClient} = useServices();
    const [yAxisMax, setYAxisMax] = useState(180);
    const [selectedUserId, setSelectedUserId] = useState(user);
    const [selectedUserName, setSelectedUserName] = useState("Select User");
    const [showListView, setShowListView] = useState(true);
    const itemsPerLoad = 2;
    const [visibleItemCount, setVisibleItemCount] = useState(itemsPerLoad);
    const [loadAllDays, setLoadAllDays] = useState(false);
    const [showMapOverlay, setShowMapOverlay] = useState(false);
    const [avgTIRByLocation, setAvgTIRByLocation] = useState({});
    const high = 140;
    const [hasMoreData, setHasMoreData] = useState(true);

    // Fetch meals from the API, augmenting the data with TIR for each meal.
    const fetchMeals = useCallback(async (isInitialLoad = false) => {
        setIsLoading(true);
        setError(null);

        if (isInitialLoad) {
            setData([]);
        }

        let url = `/users/${selectedUserId}/meals?loadAllDays=${loadAllDays}`;
        if (startDate && endDate) {
            const formattedStartDate = dayjs(startDate).format("YYYY-MM-DD");
            const formattedEndDate = dayjs(endDate).format("YYYY-MM-DD");
            url += `&startDate=${formattedStartDate}&endDate=${formattedEndDate}`;
        }

        try {
            const response = await authorizedHttpClient.jsonRequest("GET", url);
            if (Array.isArray(response.data) && response.data.length > 0) {
                // Calculate TIR for each meal.
                const dataWithTIR = response.data.map((day) => ({
                    ...day,
                    meals: day.meals.map((meal) => {
                        const mealTime = new Date(meal.eatenAt);
                        const endTime = new Date(mealTime.getTime() + 3 * 60 * 60 * 1000); // 3 hours later
                        const TIR = calculateTimeInRange(day.timelineEvents, mealTime, endTime);
                        return {...meal, TIR};
                    }),
                }));

                // Aggregate TIR by location.
                const tirByLocation = {};
                dataWithTIR.forEach((day) => {
                    day.meals.forEach((meal) => {
                        if (meal.preparationPlace && meal.preparationPlace.location) {
                            const locationId = meal.preparationPlace.id;
                            if (!tirByLocation[locationId]) {
                                tirByLocation[locationId] = [];
                            }
                            if (meal.TIR !== undefined) {
                                tirByLocation[locationId].push(meal.TIR);
                            }
                        }
                    });
                });

                const avgTIRByLocation = {};
                Object.keys(tirByLocation).forEach((locationId) => {
                    const tirValues = tirByLocation[locationId];
                    const avgTIR = tirValues.reduce((acc, curr) => acc + curr, 0) / tirValues.length;
                    avgTIRByLocation[locationId] = avgTIR;
                });
                setAvgTIRByLocation(avgTIRByLocation);

                if (isInitialLoad) {
                    setData(dataWithTIR);
                } else {
                    setData((prevData) => [...(prevData || []), ...dataWithTIR]);
                }
            }
        } catch (error) {
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    }, [startDate, endDate, selectedUserId, authorizedHttpClient, loadAllDays]);

    // Fetch meals when loadAllDays changes.
    useEffect(() => {
        fetchMeals(false);
    }, [loadAllDays]);

    // Fetch meals when date range changes.
    useEffect(() => {
        fetchMeals(true);
    }, [startDate, endDate, fetchMeals]);

    const loadMoreItems = () => {
        if (visibleItemCount < data.length) {
            setVisibleItemCount((prevCount) => prevCount + itemsPerLoad);
        }
    };

    useEffect(() => {
        setHasMoreData(visibleItemCount < data.length);
    }, [visibleItemCount, data.length]);

    // Extract locations for the map overlay.
    const extractAndAugmentLocationsForMap = (data) => {
        const locations = data.reduce((acc, day) => {
            day.meals.forEach((meal) => {
                if (meal.preparationPlace && meal.preparationPlace.location) {
                    const {location, id, name} = meal.preparationPlace;
                    const existingLocation = acc.find((loc) => loc.id === id);
                    if (!existingLocation) {
                        acc.push({
                            ...location,
                            name: name,
                            id: id,
                            avgTIR: avgTIRByLocation[id] ? avgTIRByLocation[id].toFixed(2) : "N/A",
                        });
                    }
                }
            });
            return acc;
        }, []);
        return locations;
    };

    // Fetch the latest meal date and set the date range accordingly.
// Fetch the latest meal date just to set how far back to look:
    const getLatestDate = async () => {
        try {
            const res = await authorizedHttpClient.jsonRequest(
                'GET',
                `/users/${selectedUserId}/meals/latest-meal-date`
            );
            if (res.data) {
                const latest = dayjs(res.data);
                // endDate remains today; only roll startDate back one month from the latest meal
                setStartDate(latest.subtract(1, 'month').startOf('day'));
            }
        } catch (err) {
            console.error('Error fetching latest meal date:', err);
        }
    };

    useEffect(() => {
        getLatestDate();
    }, [selectedUserId]);

    const visibleData = useMemo(() => {
        // make a copy and sort by date, newest first
        const sorted = [...data].sort(
            (a, b) => new Date(b.date) - new Date(a.date)
        );
        return sorted.slice(0, visibleItemCount);
    }, [data, visibleItemCount]);
    const handleToggleView = () => {
        setShowListView(!showListView);
    };

    const handleUserSelect = (userId, userName) => {
        setSelectedUserId(userId);
        setSelectedUserName(userName);
    };

    if (error) {
        return <div>Error: {error}</div>;
    }

    if (isLoading || data === null) {
        const defaultOptions = {
            loop: true,
            autoplay: true,
            animationData: food_carousel,
            rendererSettings: {
                preserveAspectRatio: "xMidYMid slice",
            },
        };

        return (
            <Box>
                <Lottie options={defaultOptions} height={400} width={400}/>
                <br/>
                Hold on - you have a lot of data
                <Box className="loader"></Box>
            </Box>
        );
    }

    return (
        <div className="App">
            {/* Render the CustomMenu (sidebar) at the top */}
            <CustomMenu
                data={data}
                loadAllDays={loadAllDays}
                setLoadAllDays={setLoadAllDays}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                setYAxisMax={setYAxisMax}
                isAdmin={isAdmin}
                userRole={userRole}
                selectedUserName={selectedUserName}
                handleUserSelect={handleUserSelect}
                user={user}
                selectedUserId={selectedUserId}
                handleToggleView={handleToggleView}
                showListView={showListView}
                setShowMapOverlay={setShowMapOverlay}
                showMapOverlay={showMapOverlay}
                authorizedHttpClient={authorizedHttpClient}
                drawerWidth={drawerWidth}
            />

            {/* Main content container. On desktop, add left margin so the sidebar doesn’t overlap the content. */}
            <Box sx={{ml: {xs: 0, md: `${drawerWidth}px`}, pt: {xs: 2, md: 2}}}>
                {showMapOverlay && (
                    <Maps3
                        data={extractAndAugmentLocationsForMap(data)}
                        onClose={() => setShowMapOverlay(false)}
                    />
                )}

                {data && data.length > 0 ? (
                    <div>
                        <h1 style={{color: "#fff"}}>Glukose Report</h1>
                    </div>
                ) : (
                    <Box sx={{m: 1}}>
                        <Typography sx={{color: "#fff"}}>
                            No Data for the selected Date
                        </Typography>
                    </Box>
                )}

                <InfiniteScroll
                    dataLength={visibleData.length}
                    next={loadMoreItems}
                    hasMore={hasMoreData}
                    loader={
                        <Box sx={{height: 300, backgroundColor: "red"}}>
                            <h4>Loading more...</h4>
                        </Box>
                    }
                    endMessage={
                        <p style={{textAlign: "center"}}>You have seen it all</p>
                    }
                    scrollThreshold={0.9}
                >
                    {visibleData.map((dayData) => {
                        const meals = dayData.meals;
                        const chartData = Array.from(
                            {length: (24 * 60) / 5},
                            (_, i) => {
                                const hour = Math.floor(i / 12);
                                const minute = (i % 12) * 5;
                                return {
                                    date: new Date(dayData.date).setHours(hour, minute, 0, 0),
                                    mealValue: undefined,
                                    mealValueDetails: undefined,
                                    glucose: undefined,
                                    carbs: undefined,
                                    insulin: undefined,
                                };
                            }
                        );

                        // Process meal data into chartData
                        for (const meal of meals) {
                            const eatenAt = new Date(meal.eatenAt);
                            const eatenAtIndex =
                                eatenAt.getHours() * 12 + Math.floor(eatenAt.getMinutes() / 5);
                            chartData[eatenAtIndex].mealValue = 50;
                            chartData[eatenAtIndex].mealValueDetails = {
                                name: meal.dishes.map((dish) => dish.name).join(", "),
                                preparationPlaces: meal.dishes[0].preparationPlaces,
                                image:
                                    meal.images && meal.images.length > 0
                                        ? meal.images[0].url
                                        : null,
                            };
                        }

                        dayData.timelineEvents.forEach(event => {
                            const t = new Date(event.date);
                            const idx = t.getHours() * 12 + Math.floor(t.getMinutes() / 5);

                            if (event.type === 'glucose') {
                                chartData[idx].glucose = event.payload.value;
                            } else if (event.type === 'carbs') {
                                chartData[idx].carbs = event.payload.carbs;
                            } else if (event.type === 'insulin') {
                                chartData[idx].insulin = event.payload.amount;
                            }
                        });

                        // Process timeline events into chartData
                        const timelineEvents = dayData.timelineEvents;
                        for (const event of timelineEvents) {
                            const eventTime = new Date(event.date);
                            const eventIndex =
                                eventTime.getHours() * 12 + Math.floor(eventTime.getMinutes() / 5);
                            chartData[eventIndex].timelineValue = event.payload.value;
                        }

                        // Process symptoms and activities into chartData
                        const symptoms = dayData.symptoms;
                        const activities = dayData.activities;
                        for (const symptom of symptoms) {
                            const symptomTime = new Date(symptom.startDate);
                            const symptomIndex =
                                symptomTime.getHours() * 12 + Math.floor(symptomTime.getMinutes() / 5);
                            chartData[symptomIndex].symptomValue = symptom.intensity ? symptom.intensity * 3 : 1;
                            chartData[symptomIndex].symptomDetails = symptom;
                        }
                        for (const activity of activities) {
                            const activityTime = new Date(activity.startDate);
                            const activityIndex =
                                activityTime.getHours() * 12 + Math.floor(activityTime.getMinutes() / 5);
                            chartData[activityIndex].activityValue = activity.intensity ? activity.intensity * 3 : 1;
                            chartData[activityIndex].activityDetails = activity;
                        }

                        return (
                            <Paper sx={{mb: 4, borderRadius: 2, m: 3}} key={dayData.date}>
                                {showListView ? (
                                    <DayDataView
                                        key={dayData.date}
                                        date={dayData.date}
                                        chartData={chartData}
                                        yAxisMax={yAxisMax}
                                        high={high}
                                        meals={dayData.meals}
                                    />
                                ) : (
                                    <MealsWithGraph
                                        key={dayData.date}
                                        meals={dayData.meals}
                                        timelineEvents={dayData.timelineEvents}
                                        symptoms={dayData.symptoms}
                                        activities={dayData.activities}
                                        high={high}
                                        yAxisMax={yAxisMax}
                                        data={data}
                                    />
                                )}
                            </Paper>
                        );
                    })}
                    <Box sx={{p: 4}}></Box>
                </InfiniteScroll>
                {visibleItemCount < data.length && (
                    <Box sx={{textAlign: "center", my: 2}}>
                        <Button variant="contained" onClick={loadMoreItems}>
                            Load More
                        </Button>
                    </Box>
                )}
            </Box>
        </div>
    );
};

export default DataAnalyizer;
